import React, { useState, useEffect } from "react";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import * as Shared from "./Shared.js";

/*
 * Every version of one section (by stable identifier). The latest version (list[0], since
 * GetSectionVersionHistory sorts by Id desc) is always one side of the comparison; click
 * "Original" on any other version to compare it against that latest, via GET SectionVersionDiff.
 */

const API = "/umbraco/api/TechnicalDocumentation";

const SectionVersionHistory = ({ techDocId, techDocName, sectionIdentifier, sectionTitle, versions, onClose }) => {
    const [versionList, setVersionList] = useState([]);   // this section's _SectionVersion rows, newest first
    const [loading, setLoading] = useState(true);
    const [a, setA] = useState(null);
    const [diff, setDiff] = useState(null);
    const [diffLoading, setDiffLoading] = useState(false);

    const byId = {};
    versionList.forEach((v) => { byId[v.Id] = v; });
    const latest = versionList[0] || null;

    useEffect(() => { load(); }, [sectionIdentifier]);

    const load = () => {
        setLoading(true);
        fetch(`${API}/SectionVersions/?techDocId=${techDocId}&sectionIdentifier=${sectionIdentifier}`)
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setVersionList(list);
                // default compare: the version just before the latest
                if (list.length >= 2) setA(list[1].Id);
            })
            .catch(() => setVersionList([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!a || !latest || a === latest.Id) { setDiff(null); return; }
        setDiffLoading(true);
        fetch(`${API}/SectionVersionDiff/?techDocId=${techDocId}&aSectionVersionId=${a}&bSectionVersionId=${latest.Id}`)
            .then((r) => r.json())
            .then((d) => setDiff(d?.diffHtml || ""))
            .catch(() => setDiff(""))
            .finally(() => setDiffLoading(false));
    }, [a, latest?.Id]);

    const vLabel = (v) => `v${v.Major}.${v.Minor}`;
    const docVLabel = (v) => {
        const doc = (versions || []).find((dv) => dv.Id === v.DocumentVersionId);
        return doc ? `${techDocName || "Document"} v${doc.VersionNumber}` : "";
    };
    const stateLabel = (v) => (v.State === 1 ? "Approved" : v.State === 2 ? "Rejected" : "Draft");
    const describe = (v) => v ? `${vLabel(v)} · ${docVLabel(v) || "—"} · ${stateLabel(v)}` : "";

    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal wide" style={{ maxWidth: 1640, width: "98vw", height: "94vh", display: "flex", flexDirection: "column" }}>
                <div className="td-modal-h">
                    <h3><i className="fas fa-history" /> Version history — {sectionTitle}</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b" style={{ display: "flex", gap: 18, flex: 1, minHeight: 0, maxHeight: "none" }}>
                    <div style={{ width: 260, flexShrink: 0, overflowY: "auto" }}>
                        {loading ? <LoadingSpinnerImage /> : versionList.length === 0 ? (
                            <div className="td-muted" style={{ fontSize: 12 }}>No versions yet.</div>
                        ) : versionList.map((v) => {
                            const isLatest = v.Id === latest?.Id;
                            return (
                                <div
                                    key={v.Id}
                                    className={`td-tcard ${(a === v.Id || isLatest) ? "sel" : ""}`}
                                    onClick={() => { if (!isLatest) setA(v.Id); }}
                                    style={{ cursor: isLatest ? "default" : "pointer" }}
                                >
                                    <div className="td-row" style={{ justifyContent: "space-between" }}>
                                        <b>{sectionTitle} {vLabel(v)}</b>
                                        <Shared.SectionVersionPill state={v.State} />
                                    </div>
                                    <div className="td-muted" style={{ fontSize: 11, marginTop: 1 }}>{docVLabel(v)}</div>
                                    <div className="td-muted" style={{ fontSize: 11, marginTop: 3 }}>
                                        {v.ApprovedOn ? `Approved ${Shared.formatDate(v.ApprovedOn, true)}` : `Saved ${Shared.formatDate(v.CreatedOn, true)}`}
                                        {v.CreatedByName ? ` · ${v.CreatedByName}` : ""}
                                    </div>
                                    <div className="td-row" style={{ gap: 6, marginTop: 5 }}>
                                        {isLatest ? (
                                            <span className="td-tag" style={{ fontSize: 10 }}>Latest</span>
                                        ) : a === v.Id ? (
                                            <span className="td-tag" style={{ fontSize: 10, background: "#e8f0fb", color: "#2b6cb0" }}>Original</span>
                                        ) : null}
                                        {v.PdfFileName && (
                                            <Shared.PdfDownloadLink
                                                className="td-pdflink"
                                                style={{ marginLeft: "auto" }}
                                                href={`${API}/SectionVersionPdf/?techDocId=${techDocId}&sectionVersionId=${v.Id}`}
                                                filename={v.PdfFileName}
                                                label="PDF"
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ flex: 1, minWidth: 0, overflowY: "auto", borderLeft: "1px solid #e2e8f0", paddingLeft: 18 }}>
                        {!latest ? (
                            <div className="td-muted" style={{ fontSize: 12 }}>No versions yet.</div>
                        ) : !a ? (
                            <div className="td-muted" style={{ fontSize: 12 }}>Click <b>Original</b> on a version to compare it with the latest.</div>
                        ) : a === latest.Id ? (
                            <div className="td-muted" style={{ fontSize: 12 }}>This is the latest version.</div>
                        ) : diffLoading ? (
                            <LoadingSpinnerImage />
                        ) : (
                            <>
                                <div className="td-diffcompare">
                                    <div className="td-diffcompare-side">
                                        <div className="td-diffcompare-v">Original · {describe(byId[a])}</div>
                                    </div>
                                    <i className="fas fa-arrow-right td-muted" />
                                    <div className="td-diffcompare-side">
                                        <div className="td-diffcompare-v">Latest · {describe(latest)}</div>
                                    </div>
                                    <span className="td-muted" style={{ fontSize: 11.5, marginLeft: "auto" }}>
                                        <span className="text-deleted">removed</span> · <span className="text-added">added</span>
                                    </span>
                                </div>

                                {/* Same redlined HTML in both columns; CSS hides "added" in Original and "deleted" in Latest. */}
                                <div className="td-diffcols">
                                    <div className="td-diffcol">
                                        <div className="td-diffhead">Original · {vLabel(byId[a])}</div>
                                        <Shared.SafeHTML className="td-readonly-body td-diff-hide-added" html={diff || "<p><em>No differences.</em></p>"} />
                                    </div>
                                    <div className="td-diffcol">
                                        <div className="td-diffhead">Latest · {vLabel(latest)}</div>
                                        <Shared.SafeHTML className="td-readonly-body td-diff-hide-deleted" html={diff || "<p><em>No differences.</em></p>"} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="td-modal-f">
                    <span className="caption">Every save is kept as its own version, across every document version; approved versions keep their locked PDF.</span>
                    <button className="td-btn td-btn-cancel" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default SectionVersionHistory;
